import { useCallback, useEffect, useState } from 'react';

import { InputWrapper } from 'shared/components/form/inputs';
import { createAutocompleteService } from 'shared/util/google-maps';
import { trackClick } from 'shared/util/tracking/mercury';

import classnames from 'classnames';
import { withFormsy } from 'formsy-react';
import { debounce } from 'lodash';
import type {
  GoogleMapsAutocompleteService,
  GooglePlacePrediction,
} from 'types/google-maps';
import { Input as BootstrapInput } from 'reactstrap';

interface GooglePlaceData {
  place_id: string;
  description: string;
  title: string;
  address: string;
}

interface EnhancedPlaceData extends GooglePlaceData {
  userEnteredText: string;
  isGooglePlaceSelection: boolean;
  extractedCompanyName?: string;
}

interface GeoInputProps {
  setPlaceData: (place: GooglePlaceData) => Promise<void>;
  onPlaceSelect?: (place: EnhancedPlaceData) => void;
  onManualEntry?: (text: string, isManualEntry: boolean) => void;

  // Form input props
  label?: string;
  name: string;
  inputProps?: object;
  icon?: string | React.ReactNode;
  useTitleAsValue?: boolean;

  // withFormsy props
  isPristine?: boolean;
  isValid: boolean;
  value?: any;
  setValue: (value: any) => void;
  errorMessage?: string;
  showError: boolean;
  validatePristine?: boolean;
  validateOnBlur?: boolean;

  // InputWrapper props
  isFormDisabled?: boolean;
  getErrorMessage?: any;
  width?: boolean | number | 'auto';
  disabled?: boolean;
  newStyle?: boolean;
  labelAddon?: any;
  labelSmall?: boolean | string | React.ReactNode;
  beforeAddon?: React.ReactNode;
  afterAddon?: React.ReactNode;
  hint?: string;
  hintClassname?: string;
  hasBlurred?: boolean;
  prepend?: React.ReactNode;
  append?: React.ReactNode;
  id?: string;
  inputId?: string;
  autoCapitalize?: string;
  required?: boolean;
}

export function GeoInput(props: GeoInputProps) {
  const {
    setPlaceData,
    onPlaceSelect,
    onManualEntry,
    label,
    name,
    inputProps,
    icon,
    useTitleAsValue = false,
    isPristine,
    isValid,
    value,
    setValue,
    errorMessage,
    showError,
    validatePristine,
    validateOnBlur,
    isFormDisabled = false,
    getErrorMessage,
    width,
    disabled,
    newStyle,
    labelAddon,
    labelSmall,
    beforeAddon,
    afterAddon,
    hint,
    hintClassname,
    hasBlurred,
    prepend,
    append,
    id,
    inputId,
    autoCapitalize,
    required,
    ...restProps
  } = props;

  // State hooks replacing class state
  const [search, setSearch] = useState<string>('');
  const [places, setPlacesState] = useState<GooglePlaceData[]>([]);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [didBlur, setDidBlur] = useState<boolean>(false);
  const [didShowSuggestions, setDidShowSuggestions] = useState<boolean>(false);
  const [didHideSuggestions, setDidHideSuggestions] = useState<boolean>(false);
  const [focus, setFocus] = useState<boolean>(false);
  const [autocomplete, setAutocomplete] = useState<GoogleMapsAutocompleteService | null>(null);
  const [isGooglePlaceSelection, setIsGooglePlaceSelection] = useState<boolean>(false);

  // Initialize Google Maps AutocompleteService
  useEffect(() => {
    const autocompleteService = createAutocompleteService(window);
    if (autocompleteService) {
      setAutocomplete(autocompleteService);
    }
  }, []);

  const getGooglePredictions = useCallback((query: string): Promise<GooglePlacePrediction[] | null> => {
    if (!autocomplete) {
      return Promise.resolve(null);
    }

    const opts = {
      input: query,
      types: ['geocode', 'establishment'],
    };

    return new Promise(resolve => {
      autocomplete.getPlacePredictions(opts, (places: GooglePlacePrediction[] | null) => {
        resolve(places);
      });
    });
  }, [autocomplete]);

  const beginSearch = useCallback(
    debounce(async () => {
      try {
        const placesData = await getGooglePredictions(search);
        let processedPlaces: GooglePlaceData[] = [];

        if (placesData) {
          processedPlaces = placesData.map(place => {
            const parts = place.description.split(', ');
            const title = parts.shift() || place.description;
            const address = parts.join(', ');

            return {
              place_id: place.place_id,
              description: place.description,
              title,
              address,
            };
          });
        }

        setPlaces(processedPlaces);
      } catch (error) {
        setPlaces([]);
      }
    }, 500),
    [search, getGooglePredictions]
  );

  const setPlaces = useCallback((newPlaces: GooglePlaceData[]) => {
    if (search.length < 3) {
      return;
    }

    setPlacesState(newPlaces);

    const shouldShowSuggestions = newPlaces.length > 0 && !value;

    if (showSuggestions && !shouldShowSuggestions) {
      // Suggestions are currently shown but about to be hidden
      setDidHideSuggestions(true);
    } else if (!showSuggestions && shouldShowSuggestions) {
      // Suggestions are currently hidden but about to be shown
      setDidShowSuggestions(true);
    }

    setShowSuggestions(shouldShowSuggestions);
  }, [search, value, showSuggestions]);

  const modifyValue = useCallback(({ target }: { target: HTMLInputElement }) => {
    const inputValue = target.value;

    setSearch(inputValue);
    setValue(undefined);

    // Reset Google Place selection state when user starts typing
    setIsGooglePlaceSelection(false);

    if (inputValue.length >= 3) {
      beginSearch();
    } else {
      // If the field gets emptied out, turn off errors again
      // so they don't appear as the user types
      setDidBlur(false);
      setDidShowSuggestions(false);
      setDidHideSuggestions(false);
      setPlaces([]);
    }
  }, [setValue, beginSearch, setPlaces]);

  const setPlace = useCallback((place: GooglePlaceData) => {
    const placeValue = useTitleAsValue ? place.title : place.description;

    setValue(placeValue);
    setSearch(placeValue);

    // Track that this is a Google Place selection
    setIsGooglePlaceSelection(true);

    // Create enhanced place data for the callback
    const enhancedPlace: EnhancedPlaceData = {
      ...place,
      userEnteredText: placeValue,
      isGooglePlaceSelection: true,
      extractedCompanyName: place.title, // Use the place title as the extracted company name
    };

    setPlaceData(place);
    onPlaceSelect?.(enhancedPlace);
    setPlaces([]);
    trackClick(place.title);
  }, [setValue, useTitleAsValue, setPlaceData, onPlaceSelect, setPlaces]);

  const onBlur = useCallback(() => {
    setFocus(false);
    // If the field has been blurred, show error messages
    setDidBlur(true);
    setValue(search);

    // Call onManualEntry only if this is not a Google Place selection
    if (!isGooglePlaceSelection && search.trim()) {
      onManualEntry?.(search, true);
    }
  }, [search, setValue, onManualEntry, isGooglePlaceSelection]);

  const onFocus = useCallback(() => {
    setFocus(true);
  }, []);

  const renderInput = useCallback((invalid: boolean) => {
    const style: React.CSSProperties = {};
    if (!value && !invalid) {
      style.color = '#999';
    }

    return (
      <BootstrapInput
        name={ name }
        type="text"
        invalid={ invalid }
        value={ search }
        onChange={ modifyValue }
        onBlur={ onBlur }
        onFocus={ onFocus }
        autoComplete="off"
        icon={ icon }
        style={ style }
        { ...inputProps }
      />
    );
  }, [name, search, modifyValue, onBlur, onFocus, icon, inputProps, value]);

  const renderSuggestions = useCallback(() => {
    if (!showSuggestions) {
      return null;
    }

    return (
      <div className="geo-list">
        {places.map(place => (
          <a key={ place.place_id } onClick={ () => setPlace(place) }>
            <div className="title">{place.title}</div>
            <div className="address">{place.address}</div>
          </a>
        ))}
      </div>
    );
  }, [showSuggestions, places, setPlace]);

  // The logic for this probably doesn't make sense at a glance, but I have
  // a truth table to justify it. Contact me for the full thing, but here's
  // the gist of it (true/false values for didBlur, didShow, and didHide,
  // and arrow to desired outcome):
  //
  // User types a value but hasn't done anything else:        F F F -> F
  // User types a value, clicks and holds on a suggestions:   T T F -> F
  // User types a value, clicks a suggestion completely:      T T T -> T
  // User types a value, then just clicks outside the field:  T T T -> T
  // User just blurs the field:                               T F F -> T
  //
  // - Ben Visness, 2017-10-09
  const canShowErrors = didBlur && (didShowSuggestions === didHideSuggestions);

  const invalid = canShowErrors && ((showError || !isValid) &&
    (validatePristine || !isPristine) &&
    !(validateOnBlur && !didBlur));

  return (
    <InputWrapper
      classNames={ classnames('geosuggest', { 'has-danger': invalid }) }
      onClickOutside={ () => {
        setPlaces([]);
      } }
      invalid={ invalid }
      focus={ focus }
      label={ label }
      name={ name }
      isPristine={ isPristine }
      isValid={ isValid }
      value={ value }
      setValue={ setValue }
      errorMessage={ errorMessage }
      showError={ showError }
      validatePristine={ validatePristine }
      validateOnBlur={ validateOnBlur }
      isFormDisabled={ isFormDisabled }
      getErrorMessage={ getErrorMessage }
      width={ width }
      disabled={ disabled }
      newStyle={ newStyle }
      labelAddon={ labelAddon }
      labelSmall={ labelSmall }
      beforeAddon={ beforeAddon }
      afterAddon={ afterAddon }
      hint={ hint }
      hintClassname={ hintClassname }
      hasBlurred={ hasBlurred }
      prepend={ prepend }
      append={ append }
      id={ id }
      inputId={ inputId }
      autoCapitalize={ autoCapitalize }
      required={ required }
      { ...restProps }
    >
      {renderInput(invalid)}
      {renderSuggestions()}
    </InputWrapper>
  );
}

// Default props
GeoInput.defaultProps = {
  useTitleAsValue: false,
  validatePristine: false,
  validateOnBlur: false,
  width: false,
  disabled: false,
  newStyle: false,
  labelAddon: false,
  labelSmall: false,
  beforeAddon: false,
  afterAddon: false,
  hintClassname: '',
  hasBlurred: false,
  isFormDisabled: false,
};

export default withFormsy(GeoInput);